import React, { useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowStore } from "../store/sendmoneyWorkflowStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import { SEND_MONEY_ROUTES } from "../routes/sendMoneyRoutes";
import type { StepKey } from "../sendWorkflow/types/stepKey";
import { mapiSenderAddressFieldDisplayOrder } from "../../../../constants/send.constant";
import { sendMoneyService } from "../../../../services/sendmoney.service";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import { buildUpdateTransactionRequest } from "../helper/buildUpdateTransactionRequest";
import { useSessionStore } from "../../../../../../state/src/useSessionStore";

countries.registerLocale(enLocale);

function formatPhoneNumber(number: string) {
  if (!number) return "";
  const cleaned = number.replace(/\D/g, "");
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
}

function maskId(value: string) {
  if (!value) return "";
  if (value.length <= 4) return value;
  return `${value.slice(0, 2)}${"*".repeat(value.length - 4)}${value.slice(-2)}`;
}

export function FinalReviewScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const journeyData = useJourneyStore((state) => state.journeyData);
  const activeWorkflow = useWorkflowStore((state) => state.activeWorkflow);
  const setCurrentStepKey = useWorkflowStore((state) => state.setCurrentStepKey);
  const setIsEditMode = useWorkflowStore((state) => state.setIsEditMode);
  const { goNext } = useWorkflowNavigation();
  const { executeWithLoading } = useApiWithLoading();

  const {
    phoneNumber,
    sendAmount,
    sendCurrency,
    receiveAmount,
    receiveCurrency,
    destinationCountry,
    destinationState,
    selectedServiceOption,
    senderInfo,
    senderAddress,
    senderDob,
    receiverInfo,
    additionalInfo,
  } = journeyData;

  const serviceOptionName = selectedServiceOption?.serviceOptionName || "—";

  const alpha2 = useMemo(() => {
    const a3 = (destinationCountry?.iso3Code || "").toUpperCase();
    return countries.alpha3ToAlpha2(a3) || destinationCountry?.iso2Code || "";
  }, [destinationCountry?.iso3Code, destinationCountry?.iso2Code]);

  const senderName = useMemo(() => {
    return [
      senderInfo.firstName,
      senderInfo.middleName,
      senderInfo.lastName,
      senderInfo.secondLastName,
    ]
      .filter(Boolean)
      .join(" ");
  }, [senderInfo]);

  const receiverName = useMemo(() => {
    return [
      receiverInfo.firstName,
      receiverInfo.middleName,
      receiverInfo.lastName,
      receiverInfo.secondLastName,
    ]
      .filter(Boolean)
      .join(" ");
  }, [receiverInfo]);

  const senderAddressFull = useMemo(() => {
    const m = senderAddress as unknown as Record<string, string>;
    return mapiSenderAddressFieldDisplayOrder
      .map((tag) => (m[tag] || "").trim())
      .filter(Boolean)
      .join(", ");
  }, [senderAddress]);

  const handleNext = async () => {
    const agentId = useSessionStore.getState().agentId;
    if (!agentId) return;

    const request = buildUpdateTransactionRequest(journeyData, agentId);
    if (!request) return;

    const response = await executeWithLoading(
      () => sendMoneyService.updateTransaction(request),
      "Processing..."
    );
    console.log("updateTransaction response", response);
    goNext();
  };

  // set isEditMode to true if the editBehavior is RETURN_TO_REVIEW 
  const onEditStep = (stepKey: StepKey, route: string) => {
    const step = activeWorkflow.find((s) => s.key === stepKey);
    setIsEditMode(step?.editBehavior === "RETURN_TO_REVIEW");
    setCurrentStepKey(stepKey);
    navigate(route);
  };

  useStepAction(handleNext);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      testID="final-review-screen"
    >
      <Text style={styles.title}>{t("REVIEW_TITLE")}</Text>

      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>{t("REVIEW_YOU_SEND")}</Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{sendAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{sendCurrency || "USD"}</Text>
          </View>
          <Text style={[styles.inputLabel, { marginTop: 8 }]}>
            {t("REVIEW_THEY_RECEIVE")}
          </Text>
          <View style={styles.amountInput}>
            <Text style={styles.amountText}>{receiveAmount.toFixed(2)}</Text>
            <Text style={styles.currencyText}>{receiveCurrency || "—"}</Text>
          </View>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditAmount"
          style={styles.editButton}
          onPress={() => onEditStep("AMOUNT", SEND_MONEY_ROUTES.AMOUNT)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionRow}>
        <View>
          <Text style={styles.inputLabel}>
            {t("REVIEW_DESTINATION_COUNTRY")}
          </Text>
          <View style={[styles.amountInput, { alignItems: "center" }]}>
            {Platform.OS === "web" ? (
              <View style={styles.flagOverflow}>
                {alpha2 ? (
                  <ReactCountryFlag
                    countryCode={alpha2}
                    svg
                    style={{ width: 30, height: 30, borderRadius: 50 }}
                    title={alpha2}
                  />
                ) : (
                  <Text style={{ fontSize: 16 }}>🌍</Text>
                )}
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )}
            <Text style={styles.countryText}>
              {destinationCountry?.name || "—"}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditCountry"
          style={styles.editButton}
          onPress={() => onEditStep("COUNTRY", SEND_MONEY_ROUTES.COUNTRY)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {(destinationCountry?.iso3Code === "USA" ||
        destinationCountry?.iso3Code === "CAN") && (
        <View style={styles.sectionRow}>
          <View>
            <Text style={styles.inputLabel}>{t("DESTINATION_STATE")}</Text>
            <Text style={styles.countryText}>
              {destinationState?.name || "—"}
            </Text>
          </View>
          <TouchableOpacity
            testID="sendMoneyEditState"
            style={styles.editButton}
            onPress={() => onEditStep("STATE", SEND_MONEY_ROUTES.STATE)}
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("RECEIVER_NAME")}</Text>
          <Text style={styles.pickUpText}>{receiverName || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditReceiver"
          style={styles.editButton}
          onPress={() =>
            onEditStep("RECEIVER_INFO", SEND_MONEY_ROUTES.RECEIVER_INFO)
          }
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("RECEIVE_METHOD_TITLE")}</Text>
          <Text style={styles.pickUpText}>{serviceOptionName}</Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditHowTheyReceive"
          style={styles.editButton}
          onPress={() =>
            onEditStep("HOW_THEY_RECEIVE", SEND_MONEY_ROUTES.HOW_THEY_RECEIVE)
          }
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.yourInfoStyle}>{t("YOUR_INFO")}</Text>

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("SENDER_NAME")}</Text>
          <Text style={styles.pickUpText}>{senderName || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditSenderName"
          style={styles.editButton}
          onPress={() => onEditStep("SENDER_INFO", SEND_MONEY_ROUTES.SENDER_INFO)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("SENDER_ADDRESS")}</Text>
          <Text style={styles.pickUpText}>{senderAddressFull || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditSenderAddress"
          style={styles.editButton}
          onPress={() =>
            onEditStep("SENDER_ADDRESS", SEND_MONEY_ROUTES.SENDER_ADDRESS)
          }
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("PHONE_NUMBER")}</Text>
          <Text style={styles.pickUpText}>
            {formatPhoneNumber(phoneNumber) || "—"}
          </Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditPhone"
          style={styles.editButton}
          onPress={() => onEditStep("PHONE", SEND_MONEY_ROUTES.PHONE)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      {additionalInfo.identityNumber ? (
        <View style={styles.sectionRow}>
          <View style={styles.textContainer}>
            <Text style={styles.inputLabel}>{t("ID_NUMBER")}</Text>
            <Text style={styles.pickUpText}>
              {maskId(additionalInfo.identityNumber)}
            </Text>
          </View>
          <TouchableOpacity
            testID="sendMoneyEditId"
            style={styles.editButton}
            onPress={() =>
              onEditStep("ADDITIONAL_INFO", SEND_MONEY_ROUTES.ADDITIONAL_INFO)
            }
          >
            <Text style={styles.editText}>{t("EDIT")}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <View style={styles.sectionRow}>
        <View style={styles.textContainer}>
          <Text style={styles.inputLabel}>{t("DOB")}</Text>
          <Text style={styles.pickUpText}>{senderDob || "—"}</Text>
        </View>
        <TouchableOpacity
          testID="sendMoneyEditDob"
          style={styles.editButton}
          onPress={() => onEditStep("SENDER_DOB", SEND_MONEY_ROUTES.SENDER_DOB)}
        >
          <Text style={styles.editText}>{t("EDIT")}</Text>
        </TouchableOpacity>
      </View>

      <View style={{ marginTop: 16, marginBottom: 16 }}>
        {i18n.language === "en" ? (
          <View>
            <Text style={styles.terms}>
              I agree to MoneyGram's{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Terms and Conditions
              </Text>{" "}
              and confirm I am the sole beneficial owner of the assets, and am
              acting solely on my own profile. By continuing with this
              transaction I consent to the collection, use, disclosure, and
              transfer (including cross-border transfer) of my personal
              information as described in our{" "}
              <Text style={[styles.link, { textDecorationLine: "underline" }]}>
                Privacy Policy.
              </Text>
            </Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              <Text style={styles.bold}>Exchange rate</Text>
              {"\n"}
              In addition to transfer fees, a currency exchange rate, also
              determined by MoneyGram, may also apply. MoneyGram may also make
              money from currency exchange. When selecting a money transfer
              operator, carefully compare both transfer fees and currency
              exchange rates.
            </Text>
          </View>
        ) : (
          <View>
            <Text style={styles.terms}>{t("TERMS_AND_CONDITIONS")}</Text>
            <Text style={[styles.terms, { marginTop: 16 }]}>
              {t("TERMS_EXCHANGE_POLICY")}
            </Text>
          </View>
        )}
      </View>

    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  content: {
    paddingHorizontal: 24,
    paddingBottom: 40,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 24,
  },
  yourInfoStyle: {
    fontFamily: "Rubik",
    fontSize: 20,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 26,
    marginTop: 32,
  },
  sectionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
  },
  textContainer: {
    flex: 1,
    flexShrink: 1,
    marginRight: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: "Rubik",
    color: Colors.subTitleColor,
    lineHeight: 22,
    alignSelf: "stretch",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 32,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 24,
    paddingLeft: 8,
  },
  pickUpText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  editButton: {
    backgroundColor: Colors.searchBackground,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  editText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 16,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  countryText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  terms: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: "400",
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  link: {
    color: Colors.titleTextColor,
    fontWeight: "500",
  },
  bold: {
    fontWeight: "600",
  },
});
